import concat from 'lodash/concat';
import filter from 'lodash/filter';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import union from 'lodash/union';
import keys from 'lodash/keys';
import map from 'lodash/map';
import mergeWith from 'lodash/mergeWith';
import slice from 'lodash/slice';

import {
    getOrganizersForPlaceTags as getOrganizersForPlaceTagsApi,
    followOrganizer as followOrganizerApi,
} from 'consumer-utils/redux/organizers/api';

import {
    POP_ORGANIZER,
    SET_ORGANIZERS,
    VIEW_ORGANIZER,
    SET_ORGANIZER_CATEGORIES,
} from 'consumer-utils/redux/organizers/actionTypes';

import { ORGANIZER_POPULAR_CATEGORIES } from 'consumer-utils/constants/categories';

import {
    GA_HOME_FEED_CATEGORY,
    GA_SHOW_ORGANIZERS_ACTION,
    GA_VIEW_ORGANIZER_PROFILE_ACTION,
} from '../../constants/analytics';

import { setFollowedByYouInFTF } from '../../utils/organizers';

import {
    setProfilesLoading,
    setOrganizerArticlesLoading,
} from '../app/actions';
import { getUserFollowing } from '../events/api';
import { getFiveToFollowData } from '../../__fixtures__/fiveToFollowContent';
import { transformFiveToFollowResponse } from '../utils';
import { SET_ORGANIZER_ARTICLES } from '../organizerArticle/actionTypes';
import { TABS_CONFIG } from '../../constants/tabConfig';

export const followOrganizer = (id) => () => followOrganizerApi(id);

export const setOrganizers = (data) => ({
    type: SET_ORGANIZERS,
    payload: data,
    meta: {
        analytics: {
            action: GA_SHOW_ORGANIZERS_ACTION,
            label: flatten(Object.values(data)).length,
            category: GA_HOME_FEED_CATEGORY,
        },
    },
});

const setRemainingCategories = (data) => ({
    type: SET_ORGANIZER_CATEGORIES,
    payload: data,
});

export const addOrganizers = (locationData, tags) => (dispatch, getState) => {
    const { location: userLocation } = getState();
    const { placeId } = locationData || userLocation;
    const searchTags = tags || ORGANIZER_POPULAR_CATEGORIES;
    const firstCategories = slice(searchTags, 0, 5);
    const lastCategories = slice(searchTags, 5);

    dispatch(setProfilesLoading(true));

    return getOrganizersForPlaceTagsApi(placeId, firstCategories).then(
        (response) => {
            const categoriesWithResults = filter(
                keys(response),
                (key) => !isEmpty(response[key]),
            );

            dispatch(setOrganizers(response));
            dispatch(setProfilesLoading(false));
            dispatch(
                setRemainingCategories(
                    concat(categoriesWithResults, lastCategories),
                ),
            );
        },
    );
};

export const addMoreOrganizers = () => (dispatch, getState) => {
    const {
        location: userLocation,
        organizers: { entities, organizersExcluded, remainingCategories },
    } = getState();
    const { placeId } = userLocation;
    const firstCategories = slice(remainingCategories, 0, 5);
    const lastCategories = slice(remainingCategories, 5);
    const exclude = concat(
        map(flatMap(entities), ({ organizer }) => organizer.id),
        organizersExcluded,
    );

    return getOrganizersForPlaceTagsApi(placeId, firstCategories, exclude).then(
        (response) => {
            const mergedEntities = mergeWith(
                entities,
                response,
                (objValue, srcValue) => {
                    if (!isArray(objValue)) {
                        return undefined;
                    }

                    return objValue.concat(srcValue);
                },
            );
            const categoriesWithResults = filter(
                keys(response),
                (key) => !isEmpty(response[key]),
            );

            dispatch(setOrganizers({ ...mergedEntities }));
            dispatch(
                setRemainingCategories(
                    concat(categoriesWithResults, lastCategories),
                ),
            );
        },
    );
};

export const popOrganizer = ({ categoryId, organizerId }) => (
    dispatch,
    getState,
) => {
    const {
        organizers: { entities, remainingCategories },
    } = getState();
    const GET_MORE_ORGANIZERS_THRESHOLD = 15;

    dispatch({
        type: POP_ORGANIZER,
        payload: { categoryId, organizerId },
    });

    const remainingEntities = map(
        flatMap(entities),
        ({ organizer }) => organizer.id,
    );

    if (
        !isEmpty(remainingCategories) &&
        remainingEntities.length <= GET_MORE_ORGANIZERS_THRESHOLD &&
        remainingCategories.length > 0
    ) {
        dispatch(addMoreOrganizers());
    }
};

export const viewOrganizerProfile = (organizerId, label) => (dispatch) =>
    dispatch({
        type: VIEW_ORGANIZER,
        meta: {
            analytics: {
                action: GA_VIEW_ORGANIZER_PROFILE_ACTION,
                label: label || organizerId,
                category: GA_HOME_FEED_CATEGORY,
            },
        },
    });

export const ADD_ORGANIZER_USER_FOLLOW = 'ADD_ORGANIZER_USER_FOLLOW';

export const addOrganizersUsersFollow = (payload) => ({
    type: ADD_ORGANIZER_USER_FOLLOW,
    payload,
});

const setOrganizerArticles = (data) => ({
    type: SET_ORGANIZER_ARTICLES,
    payload: transformFiveToFollowResponse(data),
});

export const getOrganizersUserFollow = () => (dispatch, getState) => {
    const {
        user: { isAuthenticated, publicId },
        content: {
            browseState: { tabKey },
        },
        organizers: { organizersUserFollow },
        location: { placeId },
    } = getState();

    let FTFContent = getFiveToFollowData()[placeId];

    let userFollowedOrganizerIds = organizersUserFollow;

    const { hasFiveToFollow } = TABS_CONFIG[tabKey];

    if (hasFiveToFollow && FTFContent) {
        dispatch(setOrganizerArticlesLoading(true));

        if (isAuthenticated) {
            if (isEmpty(userFollowedOrganizerIds)) {
                return getUserFollowing(publicId)
                    .then((results) => {
                        userFollowedOrganizerIds = union(
                            userFollowedOrganizerIds,
                            map(results.profiles, 'id'),
                        );
                        FTFContent = setFollowedByYouInFTF(
                            FTFContent,
                            userFollowedOrganizerIds,
                        );

                        dispatch(
                            addOrganizersUsersFollow(userFollowedOrganizerIds),
                        );
                        dispatch(setOrganizerArticles(FTFContent));
                        dispatch(setOrganizerArticlesLoading(false));
                    })
                    .catch(() => {
                        // Failed to getUserFollowing. Replace with the newer api actions like feed/src/redux/events/cityBrowseActions.js
                        dispatch(setOrganizerArticlesLoading(false));
                    });
            }
            FTFContent = setFollowedByYouInFTF(
                FTFContent,
                userFollowedOrganizerIds,
            );

            dispatch(addOrganizersUsersFollow(userFollowedOrganizerIds));
            dispatch(setOrganizerArticles(FTFContent));

            return dispatch(setOrganizerArticlesLoading(false));
        }
        dispatch(setOrganizerArticles(FTFContent));

        return dispatch(setOrganizerArticlesLoading(false));
    }

    dispatch(setOrganizerArticlesLoading(false));

    return dispatch(setOrganizerArticles([]));
};

export const SET_ORGANIZERS_USER_FOLLOW = 'SET_ORGANIZERS_USER_FOLLOW';
